import React, { FunctionComponent, SVGProps, useRef } from "react";
import { PolicyMenuItem } from "./PolicyMenuItem";
import "../general-home.scss";
import { ClickableDiv } from "../../../components/ClickableDiv";
import Alerts from "../../../util/Alerts";
import { Contractor, EfiModules, Insued } from "../../../interface/user.interface";
import { Alert } from "../../../interface/alert.interface";
import { usePolicyMenuStore } from "../../../store/policy-menu.store";
import { useAutoSelectProfile } from "./useAutoSelectProfile";
import { ReactComponent as IconWeb } from "../../../assets/img/icon/menu/web.svg";
import { ReactComponent as IconDocument } from "../../../assets/img/icon/menu/document-no-fill.svg";
import { PolicyExecutives } from "../components/policy-executives/PolicyExecutives";
import { useUser } from "../../../custom-hooks/useUser";

export type SVGType = FunctionComponent<SVGProps<SVGSVGElement> & { title?: string }>;

export type IPolicyItem = {
    id?: string;
    icon: SVGType;
    title: string;
    isDisabled?: boolean;
    onClick?: VoidFunction;
    component?: React.ReactElement;
} & (
    | {
          to: string;
          href?: never;
          hrefSamePage?: never;
      }
    | {
          to?: never;
          href: string;
          hrefSamePage?: boolean;
      }
    | {
          to?: never;
          href?: never;
          hrefSamePage?: never;
      }
);

type PolicyMenuModule = { name: EfiModules.Gen; member: Contractor } | { name: EfiModules.Vam; member: Insued };

export interface IPolicyMenuItem {
    module: PolicyMenuModule | null;
    name: string;
    owner: string;
    uuid: string;
    items: IPolicyItem[];
    anyRecentPolicy: boolean;
}

interface GeneralPolicyMenuProps {
    theme?: "dark" | "ligth";
    isSmall?: boolean;
    policies: IPolicyMenuItem[];
}

export const PolicyMenu: React.FC<GeneralPolicyMenuProps> = ({ theme = "dark", isSmall, policies }) => {
    const alert = useRef<Alert>(null);
    const { tabIndex: currentIndexPolicyMenu } = usePolicyMenuStore();
    const { onChangeMenuTab, setOnSelectItem } = useAutoSelectProfile();
    const { adminReportes, isAdmin, isBroker, adminTOTP } = useUser();

    const currentPolicyMenu = policies?.[currentIndexPolicyMenu] || null;
    const gapMenuItems = `gap-${isSmall ? "2-1" : "4"}`;

    return (
        <>
            <Alerts ref={alert} />
            <section className={`policy-menu d-flex flex-column gap-${isSmall ? "4" : "5"}`}>
                <nav className={"header d-flex flex-wrap"}>
                    {policies.map((policy, tabIndex) => (
                        <ClickableDiv
                            // NO SONAR
                            key={tabIndex}
                            cursor={policy.anyRecentPolicy}
                            className={`header__item text-center transition-3s-ease
                                ${policy.anyRecentPolicy ? "" : "header__item--no-policies"}
                                ${tabIndex === currentIndexPolicyMenu ? "header__item--active" : ""}`}
                            onClick={() => policy.anyRecentPolicy && onChangeMenuTab(tabIndex)}
                            title={policy.anyRecentPolicy ? "" : "Contratante o titular sin polizas activas"}
                        >
                            <div className={"px-4 pt-2-1 pb-1"}>
                                <p className={"fw-semibold m-0"}>{policy.name}</p>
                                <p className={"m-0"}>{policy.owner}</p>
                            </div>
                        </ClickableDiv>
                    ))}
                </nav>
                <article className={"menu"}>
                    <div className={`menu__grid ${gapMenuItems}`}>
                        {currentPolicyMenu?.items.map((item, index) => (
                            // NO SONAR
                            <ClickableDiv key={index} onClick={() => setOnSelectItem(currentPolicyMenu)}>
                                <PolicyMenuItem policy={item} isSmall={isSmall} />
                            </ClickableDiv>
                        ))}
                    </div>
                </article>
                <div className={`menu__links ${theme}`} />
                <section className={`d-flex flex-wrap justify-content-center ${gapMenuItems}`}>
                    <PolicyMenuItem
                        itemHeight={"auto"}
                        policy={{
                            title: "Página Web",
                            icon: IconWeb,
                            href: "https://www.prodeseg.com/",
                        }}
                        iconTheme={"dark"}
                        isSmall={isSmall}
                    />
                    {!isSmall ? <PolicyExecutives /> : null}
                    {adminReportes && (
                        <PolicyMenuItem
                            itemHeight={"auto"}
                            policy={{
                                title: "Reportes",
                                icon: IconDocument,
                                to: "/vam/reportes",
                            }}
                            iconTheme={"dark"}
                            isSmall={isSmall}
                        />
                    )}
                    {(isAdmin || isBroker) && (
                        <PolicyMenuItem
                            itemHeight={"auto"}
                            policy={{
                                title: "Prodeseg BI",
                                icon: IconDocument,
                                to: "/vam/bi",
                            }}
                            iconTheme={"dark"}
                            isSmall={isSmall}
                        />
                    )}
                    {adminTOTP && (
                        <PolicyMenuItem
                            itemHeight={"auto"}
                            policy={{
                                title: "MFA",
                                icon: IconDocument,
                                to: "/vam/admin-totp",
                            }}
                            iconTheme={"dark"}
                            isSmall={isSmall}
                        />
                    )}
                </section>
            </section>
        </>
    );
};
